"use client";

import { useEffect, useState } from "react";
import style from "./style.module.scss";
import {
  CURRENT_LOCATION_ZOOM,
  LAYERS,
  IMAGE_PATHS,
  GENERAL,
} from "@/constants";
import Image from "next/image";
import { Nullable } from "@/types";
import { getNavigatorPermissions } from "@/helpers/permissions-helpers";
import { IMapLatLng, IMapLatLngFlexible } from "@/interfaces/Map";

const GRANTED = "granted";
const GEOLOCATION = "geolocation";

type Props = {
  map: Nullable<google.maps.Map>;
  eventCalled: () => void;
  setMapCenter: (
    center: Nullable<IMapLatLngFlexible>,
    zoom?: number
  ) => boolean;
  setUserLocation: (pos: IMapLatLng) => void;
  minZoom: number;
  onOpenDownloadPopup?: () => void;
  isTrackingLocation?: boolean;
  startTrackingLocation?: () => void;
  stopTrackingLocation?: () => void;
};

const Control = ({
  map,
  eventCalled,
  setMapCenter,
  setUserLocation,
  minZoom,
  onOpenDownloadPopup,
  isTrackingLocation = false,
  startTrackingLocation,
  stopTrackingLocation,
}: Props) => {
  const [showLayers, setShowLayers] = useState(false);
  const [hasPermission, setHasPermission] = useState(true);

  const handleZoomIn = () => {
    eventCalled();
    if (map) {
      const currentZoom = map.getZoom();
      map.setZoom((currentZoom || 0) + 1);
    }
  };

  const handleZoomOut = () => {
    eventCalled();
    if (map) {
      const currentZoom = map.getZoom() || 0;
      if (currentZoom > minZoom) {
        map.setZoom(currentZoom - 1);
      }
    }
  };

  const handleTogleLayers = () => {
    setShowLayers((prev) => !prev);
    eventCalled();
  };

  const showPosition = (position: GeolocationPosition) => {
    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    const newCenter = { lat: +latitude, lng: +longitude };
    setMapCenter(newCenter, CURRENT_LOCATION_ZOOM);
    setUserLocation(newCenter);
  };

  const showError = (error: GeolocationPositionError) => {
    if (error.code === error.PERMISSION_DENIED) {
      setHasPermission(false);
    }
  };

  const goToCurrentLocation = () => {
    if (typeof navigator !== "undefined" && GEOLOCATION in navigator) {
      if (isTrackingLocation && stopTrackingLocation) {
        stopTrackingLocation();
      } else {
        navigator.geolocation.getCurrentPosition(showPosition, showError);
        if (startTrackingLocation) {
          startTrackingLocation();
        }
      }
    }
  };

  const handleChangeLayer = () => {
    if (onOpenDownloadPopup) {
      onOpenDownloadPopup();
    }
  };

  useEffect(() => {
    const permissions = getNavigatorPermissions();
    if (!permissions) return;
    const promise: Promise<PermissionStatus> = permissions.query({
      name: GEOLOCATION,
    });
    promise.then((permissionStatus) => {
      permissionStatus.onchange = () => {
        if (permissionStatus.state === GRANTED) {
          setHasPermission(true);
        } else {
          setHasPermission(false);
        }
      };
    });
  }, []);

  return (
    <div className={style.controlContainer}>
      {showLayers && (
        <div className={style.layers}>
          {LAYERS.map((option) => (
            <button
              key={option.id}
              className={`${style.controlButton}`}
              onClick={() => handleChangeLayer()}
            >
              <Image
                src={option.src}
                alt={option.name}
                width={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
                height={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
              />
            </button>
          ))}
        </div>
      )}

      <button className={style.controlButton} onClick={handleTogleLayers}>
        <Image
          src={IMAGE_PATHS.ICONS.LAYERS}
          alt="layers"
          width={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
          height={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
        />
      </button>
      <button
        className={`${style.controlButton} ${
          isTrackingLocation ? style.trackingActive : ""
        }`}
        disabled={!hasPermission}
        onClick={goToCurrentLocation}
      >
        <Image
          src={IMAGE_PATHS.ICONS.LOCATION}
          alt="location"
          width={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
          height={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
        />
      </button>
      <div className={style.zoomControlContainer}>
        <button className={style.zoomButton} onClick={handleZoomIn}>
          <Image
            src={IMAGE_PATHS.ICONS.PLUS}
            alt="zoom in"
            width={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
            height={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
          />
        </button>
        <button className={style.zoomButton} onClick={handleZoomOut}>
          <Image
            src={IMAGE_PATHS.ICONS.MINUS}
            alt="zoom in"
            width={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
            height={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
          />
        </button>
      </div>
    </div>
  );
};

export default Control;
